/**
 * Admin analytics endpoints — typed helpers for /admin/analytics/*.
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.9). Powers
 * TabAnalytics + ingest health badge + CSV downloads.
 *
 * Re-exported from services/api.ts so existing call-sites keep working.
 */

import { apiClient } from '../client';

// ─── Filters / response shape ───────────────────────────────────────────────

export type AnalyticsFilters = {
  date_from?: string | null; // ISO 8601 or YYYY-MM-DD
  date_to?: string | null;
  country_code?: string | null;
  countries_in?: string[] | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  device_type?: 'mobile' | 'tablet' | 'desktop' | null;
  age_category?: string | null;
  breed_size?: string | null;
  pathologies_any?: string[] | null;
  only_completed?: boolean;
};

function analyticsQuery(filters: AnalyticsFilters): string {
  const p = new URLSearchParams();
  if (filters.date_from) p.set('date_from', filters.date_from);
  if (filters.date_to) p.set('date_to', filters.date_to);
  if (filters.country_code) p.set('country_code', filters.country_code);
  if (filters.countries_in?.length) p.set('countries_in', filters.countries_in.join(','));
  if (filters.utm_source) p.set('utm_source', filters.utm_source);
  if (filters.utm_medium) p.set('utm_medium', filters.utm_medium);
  if (filters.utm_campaign) p.set('utm_campaign', filters.utm_campaign);
  if (filters.device_type) p.set('device_type', filters.device_type);
  if (filters.age_category) p.set('age_category', filters.age_category);
  if (filters.breed_size) p.set('breed_size', filters.breed_size);
  if (filters.pathologies_any?.length) p.set('pathologies_any', filters.pathologies_any.join(','));
  if (filters.only_completed) p.set('only_completed', 'true');
  return p.toString();
}

export type AnalyticsResponse<T> = {
  filters_applied: Record<string, unknown>;
  total_sessions?: number;
  data?: T[];
  steps?: T[];
} & Record<string, unknown>;

// ─── Endpoints ──────────────────────────────────────────────────────────────

export const adminAnalyticsOverview = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/overview${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsFunnel = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/funnel${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsSegmentationAge = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/segmentation/age${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsSegmentationBreed = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/segmentation/breed${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsSegmentationPathology = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/segmentation/pathology${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsSegmentationWeight = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/segmentation/weight${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsMarketing = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/marketing${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsCountries = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/countries${qs ? '?' + qs : ''}`);
  return r.data;
};

export const adminAnalyticsBehavior = async (filters: AnalyticsFilters) => {
  const qs = analyticsQuery(filters);
  const r = await apiClient.get(`/admin/analytics/behavior${qs ? '?' + qs : ''}`);
  return r.data;
};

export interface AnalyticsIngestHealth {
  processed_ok: number;
  processed_fail: number;
}

export const adminAnalyticsIngestHealth = async (): Promise<AnalyticsIngestHealth> => {
  const r = await apiClient.get<AnalyticsIngestHealth>('/admin/analytics/ingest-health');
  return r.data;
};

/**
 * Returns a URL that, when navigated to / opened, triggers a CSV
 * download. Use with `window.open(...)` or an `<a href={...} download>`
 * element.
 */
export function adminAnalyticsCsvUrl(endpoint: string, filters: AnalyticsFilters): string {
  const qs = analyticsQuery(filters);
  const params = qs ? qs + '&format=csv' : 'format=csv';
  const base = (process.env.REACT_APP_API_URL || '/api/v1').replace(/\/$/, '');
  return `${base}/admin/analytics/${endpoint}?${params}`;
}
